import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Instagram, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const goToCatalogue = () => {
    navigate('/catalogue');
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Top Announcement Bar */}
      <a href="/catalogue" className="block bg-green-600 text-white text-center py-2 text-sm font-semibold fixed top-0 left-0 right-0 z-[60] hover:bg-green-700 transition-colors cursor-pointer">
        Browse Our Full Catalogue — View Catalogue!
      </a>
      <header
        className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="https://customer-assets.emergentagent.com/job_alf-embroidery/artifacts/mrd35xh5_WhatsApp%20Image%202026-02-24%20at%204.41.47%20AM.jpeg"
              alt="Alf Uniform Logo"
              className="h-14 md:h-20 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-900 transition-colors duration-200 font-medium">Home</button>
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-900 transition-colors duration-200 font-medium">Services</button>
            <button onClick={() => scrollToSection('catalog')} className="text-gray-700 hover:text-blue-900 transition-colors duration-200 font-medium">Catalogue</button>
            <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:text-blue-900 transition-colors duration-200 font-medium">Real Projects</button>
            <button onClick={() => scrollToSection('industries')} className="text-gray-700 hover:text-blue-900 transition-colors duration-200 font-medium">Industries</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-900 transition-colors duration-200 font-medium">Contact</button>
            <button
              onClick={goToCatalogue}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-1.5 transition-colors"
            >
              <BookOpen className="h-4 w-4" />
              Catalogue
            </button>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:+96566902952" className="text-blue-900 hover:text-blue-700 transition-colors">
              <Phone className="h-5 w-5" />
            </a>
            <a href="https://instagram.com/alfuniform.kw" target="_blank" rel="noopener noreferrer" className="text-blue-900 hover:text-blue-700 transition-colors">
              <Instagram className="h-5 w-5" />
            </a>
            <Button onClick={() => scrollToSection('contact')} className="bg-blue-900 hover:bg-blue-800 text-white">
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-700 p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t max-h-[calc(100vh-4rem)] overflow-y-auto">
            <nav className="flex flex-col space-y-1">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors duration-200 font-medium text-left px-4 py-3 rounded-lg">Home</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors duration-200 font-medium text-left px-4 py-3 rounded-lg">Services</button>
              <button onClick={() => scrollToSection('catalog')} className="text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors duration-200 font-medium text-left px-4 py-3 rounded-lg">Catalogue</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors duration-200 font-medium text-left px-4 py-3 rounded-lg">Real Projects</button>
              <button onClick={() => scrollToSection('industries')} className="text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors duration-200 font-medium text-left px-4 py-3 rounded-lg">Industries</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors duration-200 font-medium text-left px-4 py-3 rounded-lg">Contact</button>
              <button
                onClick={goToCatalogue}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold text-left px-4 py-3 rounded-lg flex items-center gap-2 mx-4 justify-center"
              >
                <BookOpen className="h-5 w-5" />
                View Catalogue
              </button>
              <div className="flex items-center space-x-4 pt-3 px-4">
                <a href="tel:+96566902952" className="text-blue-900 p-2 hover:bg-blue-50 rounded-lg"><Phone className="h-6 w-6" /></a>
                <a href="https://instagram.com/alfuniform.kw" target="_blank" rel="noopener noreferrer" className="text-blue-900 p-2 hover:bg-blue-50 rounded-lg"><Instagram className="h-6 w-6" /></a>
              </div>
              <Button onClick={() => scrollToSection('contact')} className="bg-blue-900 hover:bg-blue-800 text-white w-full mt-3 py-4 text-base">
                Get a Quote
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;
